import React from 'react';
import { defineMessages, FormattedMessage, useIntl, type NoMessageValues } from 'react-intl';
import { StyleSheet, View } from 'react-native';

import { BodyText, Radio, theme } from '@sg/garnish';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationDetailsDressingMode = (props: CustomizationDetailsDressingModeProps) => {
  const {
    dressingModes = DRESSING_MODES,
    dressingMode,
    isDressingModeChangeNotAllowed,
    hasNoSelectedDressing,
    isDisabled,
    setDressingModeToOnTheSide,
    setDressingModeToMixedIn,
  } = props;

  const { formatMessage } = useIntl();
  const helperText = useHelperText(props);

  return (
    <View style={styles.container}>
      <BodyText sizeMatch={['22']} style={styles.title}>
        <FormattedMessage {...messages.title} />
      </BodyText>

      <Radio.Group gap={theme.spacing['2']} itemsPerRow={2}>
        {dressingModes.map((mode) => (
          <Radio.Item
            key={mode}
            value={mode}
            palette="spinach"
            style={styles.radioItem}
            isSelected={dressingMode === mode}
            isDisabled={
              isDisabled ||
              (mode === 'mixedIn' && isDressingModeChangeNotAllowed) ||
              (mode === 'onTheSide' && hasNoSelectedDressing)
            }
            onPress={mode === 'onTheSide' ? setDressingModeToOnTheSide : setDressingModeToMixedIn}
            label={formatMessage(messages.label, { mode })}
            accessibilityLabel={formatMessage(messages.a11yLabel, { mode })} // prettier-ignore
          />
        ))}
      </Radio.Group>

      {helperText ? <BodyText sizeMatch={['14']}>{helperText}</BodyText> : null}
    </View>
  );
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const useHelperText = (props: CustomizationDetailsDressingModeProps) => {
  const {
    dressingMode,
    isDressingModeChangeNotAllowed,
    hasNoSelectedDressing,
    isDelivery,
    isOutpost,
  } = props;

  const { formatMessage } = useIntl();

  if (dressingMode === 'mixedIn' && !isDressingModeChangeNotAllowed) {
    return formatMessage(messages.helperTextMixedIn);
  }

  if (isDelivery) {
    return formatMessage(messages.helperTextDelivery);
  }

  if (isOutpost) {
    return formatMessage(messages.helperTextOutpost);
  }

  if (isDressingModeChangeNotAllowed && !hasNoSelectedDressing) {
    return formatMessage(messages.helperTextCannotMix);
  }
};

// ─── Constants ───────────────────────────────────────────────────────────────

const DRESSING_MODES: DressingMode[] = ['onTheSide', 'mixedIn'];

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly title: NoMessageValues;
  readonly label: { readonly mode: string };
  readonly a11yLabel: { readonly mode: string };
  readonly helperTextMixedIn: NoMessageValues;
  readonly helperTextCannotMix: NoMessageValues;
  readonly helperTextDelivery: NoMessageValues;
  readonly helperTextOutpost: NoMessageValues;
}>({
  title: {
    defaultMessage: 'How would you like your dressing?',
    description: 'Customization | Dressing details | Dressing mode title',
  },
  label: {
    defaultMessage: `{mode, select,
      onTheSide {On the side}
      mixedIn {Mixed in}
      other {}
    }`,
    description: 'Customization | Dressing details | Dressing mode label',
  },
  a11yLabel: {
    defaultMessage: `{mode, select,
      onTheSide {Select dressing on the side}
      mixedIn {Select dressing mixed in}
      other {}
    }`,
    description: 'Customization | Dressing details | Dressing mode a11y label',
  },
  helperTextMixedIn: {
    defaultMessage: 'For optimal freshness, eat within 15 minutes of pickup time.',
    description: 'Customization | Dressing details | Mixed in helper text',
  },
  helperTextCannotMix: {
    defaultMessage: 'For optimal freshness, dressing for plates are served on the side.',
    description: 'Customization | Dressing details | Cannot mix helper text',
  },
  helperTextDelivery: {
    defaultMessage: 'For optimal freshness, dressing for delivery orders are served on the side.',
    description: 'Customization | Dressing details | Delivery helper text',
  },
  helperTextOutpost: {
    defaultMessage: 'For optimal freshness, dressing for outpost orders are served on the side.',
    description: 'Customization | Dressing details | Outpost helper text',
  },
});

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    rowGap: theme.spacing['2'],
  },
  title: {
    marginBottom: theme.spacing['2'],
  },
  radioItem: {
    minHeight: 64,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationDetailsDressingModeProps = {
  dressingModes?: DressingMode[];
  dressingMode: DressingMode;
  maxDressingsPortions: number;
  isDisabled: boolean;
  isDressingModeChangeNotAllowed: boolean;
  isDelivery: boolean;
  isOutpost: boolean;
  hasNoSelectedDressing: boolean;
  setDressingModeToOnTheSide: () => void;
  setDressingModeToMixedIn: () => void;
};

type DressingMode = 'onTheSide' | 'mixedIn';
